/**
 * TrySDKLive — interactive 60-second sandbox on /developers.
 * ============================================================
 * Visitor flow:
 *   1. (Optional) Drop email → we tag the lead.
 *   2. Click "Generate sandbox key" → POST /api/dev-sandbox/key
 *   3. We pre-fill a copy-ready cURL snippet with their real key.
 *   4. They run the curl in their terminal — or click "Send test event"
 *      which fires it from the browser.
 *   5. The events tile polls /api/dev-sandbox/recent-events and shows
 *      the event the moment it lands.
 *
 * Key auto-expires in 24h. No signup, no friction.
 *
 * Brand: Slate-950, emerald accents, soft amber for warnings.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Sparkles, Copy, Check, Loader2, Zap, Terminal, Activity,
  ArrowRight, Send, Clock, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtAgo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const sec = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
    if (sec < 60) return `${sec}s ago`;
    const m = Math.round(sec / 60);
    if (m < 60) return `${m}m ago`;
    return d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const TrySDKLive = () => {
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [sandbox, setSandbox] = useState(null); // { plaintext_key, sandbox_id, expires_at }
  const [events, setEvents] = useState([]);
  const [polling, setPolling] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const prefix = sandbox?.plaintext_key?.split('_')?.[1] || '';
  const trackEndpoint = `${API_URL.replace(/\/api$/, '')}/api/ai-cost-events/track`;

  const curlSnippet = sandbox ? `curl -X POST "${trackEndpoint}" \\
  -H "Authorization: Bearer ${sandbox.plaintext_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "feature_slug": "summarise",
    "cost_usd": 0.0042,
    "calls": 1,
    "model": "gpt-5.2"
  }'` : '';

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_URL}/dev-sandbox/key`, {
        label: 'Live demo from /developers',
        email: email.trim() || undefined,
      });
      setSandbox(data);
      toast.success('Sandbox key ready — copy the cURL or click Send test event');
      // Start polling for events
      pollRef.current = setInterval(() => fetchRecent(data.plaintext_key.split('_')[1]), 2500);
      setPolling(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create sandbox key — please retry.');
    } finally {
      setCreating(false);
    }
  };

  const fetchRecent = async (px) => {
    try {
      const { data } = await axios.get(`${API_URL}/dev-sandbox/recent-events`, {
        params: { prefix: px, limit: 8 },
      });
      setEvents(data.events || []);
      if (data.expired) stopPolling();
    } catch { /* swallow — sandbox might be cleaning up */ }
  };

  const handleSend = async () => {
    if (!sandbox) return;
    setSending(true);
    try {
      await axios.post(trackEndpoint,
        {
          feature_slug: 'summarise',
          cost_usd: 0.0042,
          calls: 1,
          model: 'gpt-5.2',
        },
        { headers: { Authorization: `Bearer ${sandbox.plaintext_key}` } });
      toast.success('Event sent — watch the tile light up below');
      fetchRecent(prefix);
    } catch (e) {
      toast.error('Send failed — sandbox may have expired.');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(curlSnippet);
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed — please select manually'); }
  };

  return (
    <Card className="bg-slate-950 border-emerald-500/40 shadow-xl shadow-emerald-500/5" data-testid="try-sdk-live">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
            <Zap className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              Try the SDK in 60 seconds
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                LIVE
              </Badge>
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs">
              Generate a 24-hour throwaway key, fire one cURL, watch the event land here in real time.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Step 1 — generate key */}
        {!sandbox && (
          <div className="space-y-3" data-testid="sdk-live-step-1">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com (optional — we'll send the SDK link)"
                className="bg-slate-950 border-white/10 text-slate-100 placeholder:text-slate-400"
                data-testid="sdk-live-email-input"
              />
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold whitespace-nowrap"
                data-testid="sdk-live-generate-btn"
              >
                {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</>
                  : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate sandbox key</>}
              </Button>
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              No signup. Key works for 24h. Delete after testing or it auto-expires.
            </p>
          </div>
        )}

        {/* Step 2 — show key + curl */}
        {sandbox && (
          <div className="space-y-3" data-testid="sdk-live-step-2">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5 text-emerald-400" />
                Sandbox key valid until
                <code className="text-emerald-300">
                  {new Date(sandbox.expires_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </code>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { stopPolling(); setSandbox(null); setEvents([]); }}
                className="text-slate-400 hover:text-white text-xs h-8"
                data-testid="sdk-live-reset"
              >
                Reset
              </Button>
            </div>

            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCopy}
                className="absolute top-2 right-2 z-10 text-slate-400 hover:text-white hover:bg-white/5 h-7 px-2"
                data-testid="sdk-live-copy"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="ml-1 text-xs">{copied ? 'Copied' : 'Copy curl'}</span>
              </Button>
              <pre className="bg-slate-950 border border-emerald-500/20 rounded-lg p-4 overflow-x-auto text-xs text-emerald-100/90 font-mono leading-relaxed pr-24">
                <code data-testid="sdk-live-curl">{curlSnippet}</code>
              </pre>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <Button
                onClick={handleSend}
                disabled={sending}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="sdk-live-send-event"
              >
                {sending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending</>
                  : <><Send className="w-4 h-4 mr-1.5" /> Send a test event</>}
              </Button>
              <span className="text-xs text-slate-400">
                Or paste the cURL into your terminal — events appear below in 2 sec.
              </span>
            </div>
          </div>
        )}

        {/* Step 3 — live event tile */}
        {sandbox && (
          <div className="space-y-2" data-testid="sdk-live-step-3">
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4 text-cyan-300" />
              <span className="text-xs font-semibold text-slate-300 uppercase tracking-widest">
                Live event stream
              </span>
              {polling && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  polling
                </span>
              )}
            </div>
            <div className="bg-slate-950 border border-white/5 rounded-lg p-3 min-h-[120px] max-h-[280px] overflow-y-auto">
              {events.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Activity className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  Waiting for your first event…
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {events.map((e) => (
                    <li key={e.id} className="py-2 flex items-center gap-3 text-xs" data-testid={`sdk-live-event-${e.id}`}>
                      <code className="text-emerald-300 flex-shrink-0">{e.feature_slug}</code>
                      <span className="text-slate-400 flex-shrink-0">${(e.cost_usd || 0).toFixed(4)}</span>
                      {e.model && <span className="text-cyan-300 flex-shrink-0">{e.model}</span>}
                      <span className="text-slate-400 ml-auto flex-shrink-0">{fmtAgo(e.ingested_at || e.occurred_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Events also appear in your Token Budgets, Daily Brief, and Customer P&amp;L the moment they land.
              <a href="/login" className="text-emerald-300 hover:text-emerald-200 ml-1 inline-flex items-center gap-0.5">
                Sign up <ArrowRight className="w-3 h-3" />
              </a>{' '}to keep them.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrySDKLive;
